import React, {
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Input as AntInput } from "antd";
import type { TextAreaRef } from "antd/es/input/TextArea";
import { cn } from "@/lib/utils";
import { colors } from "../../theme/colors";
import {
  FormattingToolbar,
  type FormatType,
  type FormatButton,
} from "./FormattingToolbar";
import {
  VariableChipEditor,
  type VariableChipEditorRef,
} from "./VariableChipEditor";

const { TextArea } = AntInput;

export type RichTextAreaSize = "small" | "medium" | "large";
export type RichTextAreaVariant = "outlined" | "filled" | "borderless";

// Expose methods via ref
export interface RichTextAreaRef {
  /** Insert text at the current cursor position */
  insertAtCursor: (text: string) => void;
  /** Get current cursor position */
  getCursorPosition: () => number;
  /** Set cursor position */
  setCursorPosition: (position: number) => void;
  /** Focus the textarea */
  focus: () => void;
}

export interface RichTextAreaProps {
  /** Label for the textarea */
  label?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Helper text below the field */
  helperText?: string;
  /** Error message */
  error?: string;
  /** Size variant */
  size?: RichTextAreaSize;
  /** Style variant */
  variant?: RichTextAreaVariant;
  /** Current value */
  value?: string;
  /** Default value */
  defaultValue?: string;
  /** Change handler */
  onChange?: (value: string) => void;
  /** Focus handler */
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  /** Blur handler */
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Number of rows */
  rows?: number;
  /** Auto-sizing configuration */
  autoSize?: boolean | { minRows?: number; maxRows?: number };
  /** Maximum length */
  maxLength?: number;
  /** Whether to show character count in toolbar */
  showCount?: boolean;
  /** Whether to show emoji picker button */
  showEmojiButton?: boolean;
  /** Custom format buttons */
  formatButtons?: FormatButton[];
  /** Disabled format types */
  disabledFormats?: FormatType[];
  /** Format types to hide from the toolbar entirely (vs `disabledFormats`,
   *  which dims the button but still renders it) */
  hiddenFormats?: FormatType[];
  /** Whether to show the formatting toolbar (default: true) */
  showToolbar?: boolean;
  /** Whether to allow manual resizing (default: false) */
  resizable?: boolean;
  /** Custom className */
  className?: string;
  /** Full width */
  fullWidth?: boolean;
  /** ID for the textarea */
  id?: string;
  /** When provided, enables chip mode: variables are rendered as styled chips showing labels */
  variableLabelMap?: Record<string, string>;
  /** Extra content rendered at the end of the formatting toolbar (e.g. "Use field" link) */
  toolbarExtra?: React.ReactNode;
}

// Size configuration - matches Input component
const sizeConfig = {
  small: { fontSize: "0.875rem", padding: 10 },
  medium: { fontSize: "0.9375rem", padding: 12 },
  large: { fontSize: "1rem", padding: 16 },
} as const;

export const RichTextArea = forwardRef<RichTextAreaRef, RichTextAreaProps>(
  (
    {
      label,
      required = false,
      helperText,
      error,
      size = "medium",
      variant = "filled",
      value = "",
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      placeholder,
      disabled = false,
      rows = 6,
      autoSize,
      maxLength = 1024,
      showCount = true,
      showEmojiButton = true,
      formatButtons,
      disabledFormats,
      hiddenFormats,
      showToolbar = true,
      resizable = false,
      className = "",
      fullWidth = true,
      id,
      variableLabelMap,
      toolbarExtra,
    },
    ref,
  ) => {
    const textAreaRef = useRef<TextAreaRef>(null);
    const chipEditorRef = useRef<VariableChipEditorRef>(null);
    const isChipMode = !!variableLabelMap;
    const [isFocused, setIsFocused] = React.useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = React.useState(false);
    const [internalValue, setInternalValue] = React.useState(
      defaultValue || "",
    );
    // Store cursor position for inserting text when modal is open
    const lastCursorPositionRef = useRef<number>(0);

    const currentValue = value !== undefined ? value : internalValue;
    const isFilled = Boolean(currentValue && String(currentValue).length > 0);

    // Helper to get the actual DOM textarea element from Ant Design's ref
    const getTextAreaElement = useCallback((): HTMLTextAreaElement | null => {
      const ref = textAreaRef.current;
      if (!ref) return null;
      // Ant Design's TextArea ref has the DOM element nested inside resizableTextArea.textArea
      return ref.resizableTextArea?.textArea || null;
    }, []);

    // Expose methods via ref for external use (e.g., inserting variables at cursor)
    useImperativeHandle(
      ref,
      () => ({
        insertAtCursor: (text: string) => {
          if (isChipMode && chipEditorRef.current) {
            chipEditorRef.current.insertAtCursor(text);
            return;
          }
          const textarea = getTextAreaElement();
          // Use stored cursor position if textarea is not focused (e.g., when modal is open)
          const position =
            textarea?.selectionStart ?? lastCursorPositionRef.current;
          const newText =
            currentValue.substring(0, position) +
            text +
            currentValue.substring(position);

          if (value === undefined) {
            setInternalValue(newText);
          }
          onChange?.(newText);

          // Set cursor position after the inserted text
          const newCursorPos = position + text.length;
          lastCursorPositionRef.current = newCursorPos;

          // Focus and set cursor position after React updates
          setTimeout(() => {
            const ta = getTextAreaElement();
            if (ta) {
              ta.focus();
              ta.setSelectionRange(newCursorPos, newCursorPos);
            }
          }, 0);
        },
        getCursorPosition: () => {
          if (isChipMode && chipEditorRef.current) {
            return chipEditorRef.current.getCursorPosition();
          }
          const textarea = getTextAreaElement();
          return textarea?.selectionStart ?? lastCursorPositionRef.current;
        },
        setCursorPosition: (position: number) => {
          if (isChipMode && chipEditorRef.current) {
            chipEditorRef.current.setCursorPosition(position);
            return;
          }
          lastCursorPositionRef.current = position;
          const textarea = getTextAreaElement();
          if (textarea) {
            textarea.focus();
            textarea.setSelectionRange(position, position);
          }
        },
        focus: () => {
          if (isChipMode && chipEditorRef.current) {
            chipEditorRef.current.focus();
            return;
          }
          const textarea = getTextAreaElement();
          textarea?.focus();
        },
      }),
      [currentValue, value, onChange, getTextAreaElement, isChipMode],
    );

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      // Save cursor position before blur (useful when opening modals)
      const textarea = getTextAreaElement();
      if (textarea) {
        lastCursorPositionRef.current = textarea.selectionStart ?? 0;
      }
      setIsFocused(false);
      onBlur?.(e);
    };

    const handleChipEditorFocus = useCallback(
      (e: React.FocusEvent<HTMLDivElement>) => {
        setIsFocused(true);
        // Cast to satisfy the original onFocus type (HTMLTextAreaElement -> HTMLDivElement)
        onFocus?.(e as unknown as React.FocusEvent<HTMLTextAreaElement>);
      },
      [onFocus],
    );

    const handleChipEditorBlur = useCallback(
      (e: React.FocusEvent<HTMLDivElement>) => {
        setIsFocused(false);
        onBlur?.(e as unknown as React.FocusEvent<HTMLTextAreaElement>);
      },
      [onBlur],
    );

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      if (value === undefined) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);
    };

    const insertTextAtCursor = useCallback(
      (before: string, after: string = "") => {
        const textarea = getTextAreaElement();
        if (!textarea) return;

        const start = textarea.selectionStart ?? 0;
        const end = textarea.selectionEnd ?? 0;
        const selectedText = currentValue.substring(start, end);
        const newText =
          currentValue.substring(0, start) +
          before +
          selectedText +
          after +
          currentValue.substring(end);

        if (value === undefined) {
          setInternalValue(newText);
        }
        onChange?.(newText);

        setTimeout(() => {
          textarea.focus();
          const newCursorPos = selectedText
            ? start + before.length + selectedText.length + after.length
            : start + before.length;
          textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
      },
      [currentValue, value, onChange, getTextAreaElement],
    );

    // Regex patterns for detecting list prefixes
    const UNORDERED_LIST_PATTERN = /^• /;
    const ORDERED_LIST_PATTERN = /^\d+\. /;
    const BLOCKQUOTE_PATTERN = /^> /;

    // Get the appropriate pattern for a list type
    const getListPattern = useCallback(
      (listType: "unordered" | "ordered" | "blockquote") => {
        switch (listType) {
          case "unordered":
            return UNORDERED_LIST_PATTERN;
          case "ordered":
            return ORDERED_LIST_PATTERN;
          case "blockquote":
            return BLOCKQUOTE_PATTERN;
        }
      },
      [],
    );

    // Check if a line has a specific list prefix
    const hasListPrefix = useCallback(
      (line: string, listType: "unordered" | "ordered" | "blockquote") => {
        return getListPattern(listType).test(line);
      },
      [getListPattern],
    );

    // Remove list prefix from a line
    const removeListPrefix = useCallback(
      (line: string, listType: "unordered" | "ordered" | "blockquote") => {
        return line.replace(getListPattern(listType), "");
      },
      [getListPattern],
    );

    // Format multiple lines with list prefixes (handles unordered, ordered, blockquote)
    const formatLinesWithPrefix = useCallback(
      (listType: "unordered" | "ordered" | "blockquote") => {
        const textarea = getTextAreaElement();
        if (!textarea) return;

        const selectionStart = textarea.selectionStart ?? 0;
        const selectionEnd = textarea.selectionEnd ?? 0;

        // Find the start of the first line in selection
        let firstLineStart = selectionStart;
        while (
          firstLineStart > 0 &&
          currentValue[firstLineStart - 1] !== "\n"
        ) {
          firstLineStart--;
        }

        // Find the end of the last line in selection
        let lastLineEnd = selectionEnd;
        while (
          lastLineEnd < currentValue.length &&
          currentValue[lastLineEnd] !== "\n"
        ) {
          lastLineEnd++;
        }

        // Get the text before, within, and after the selection area
        const textBefore = currentValue.substring(0, firstLineStart);
        const selectedArea = currentValue.substring(
          firstLineStart,
          lastLineEnd,
        );
        const textAfter = currentValue.substring(lastLineEnd);

        // Split selected area into lines
        const lines = selectedArea.split("\n");

        // Check if ALL non-empty lines already have the prefix (for toggle behavior)
        const nonEmptyLines = lines.filter((line) => line.trim() !== "");
        const allHavePrefix =
          nonEmptyLines.length > 0 &&
          nonEmptyLines.every((line) => hasListPrefix(line, listType));

        let formattedLines: string[];
        let totalPrefixChange = 0; // Track how much the text length changed

        if (allHavePrefix) {
          // Remove prefixes from all lines (toggle off)
          formattedLines = lines.map((line) => {
            if (line.trim() === "") return line; // Keep empty lines as-is
            const originalLength = line.length;
            const newLine = removeListPrefix(line, listType);
            totalPrefixChange -= originalLength - newLine.length;
            return newLine;
          });
        } else {
          // Add prefixes to lines that don't have them
          let orderNumber = 1;
          formattedLines = lines.map((line) => {
            // Skip empty lines but don't reset counter
            if (line.trim() === "") return line;

            // If line already has this type of prefix, keep it but update number for ordered lists
            if (hasListPrefix(line, listType)) {
              if (listType === "ordered") {
                const newLine = line.replace(
                  ORDERED_LIST_PATTERN,
                  `${orderNumber}. `,
                );
                totalPrefixChange += newLine.length - line.length;
                orderNumber++;
                return newLine;
              }
              return line; // Keep existing prefix for unordered/blockquote
            }

            // Remove any other list prefix first (switching list types)
            let cleanLine = line;
            if (hasListPrefix(line, "unordered")) {
              cleanLine = removeListPrefix(line, "unordered");
            } else if (hasListPrefix(line, "ordered")) {
              cleanLine = removeListPrefix(line, "ordered");
            } else if (hasListPrefix(line, "blockquote")) {
              cleanLine = removeListPrefix(line, "blockquote");
            }

            // Add the new prefix
            let prefix: string;
            switch (listType) {
              case "unordered":
                prefix = "• ";
                break;
              case "ordered":
                prefix = `${orderNumber}. `;
                orderNumber++;
                break;
              case "blockquote":
                prefix = "> ";
                break;
            }

            totalPrefixChange +=
              prefix.length - (line.length - cleanLine.length);
            return prefix + cleanLine;
          });
        }

        const newSelectedArea = formattedLines.join("\n");
        const newText = textBefore + newSelectedArea + textAfter;

        if (value === undefined) {
          setInternalValue(newText);
        }
        onChange?.(newText);

        // Calculate new cursor/selection positions
        setTimeout(() => {
          textarea.focus();

          if (selectionStart === selectionEnd) {
            // No selection - just cursor: move cursor by the change in first line
            const firstLineChange = formattedLines[0].length - lines[0].length;
            const newCursorPos = Math.max(
              firstLineStart,
              selectionStart + firstLineChange,
            );
            textarea.setSelectionRange(newCursorPos, newCursorPos);
          } else {
            // Has selection - maintain selection over the formatted area
            const newSelectionEnd = firstLineStart + newSelectedArea.length;
            textarea.setSelectionRange(firstLineStart, newSelectionEnd);
          }
        }, 0);
      },
      [
        currentValue,
        value,
        onChange,
        getTextAreaElement,
        hasListPrefix,
        removeListPrefix,
      ],
    );

    const handleFormatChipMode = useCallback((format: FormatType) => {
      if (!chipEditorRef.current) return;
      const formatMap: Record<string, [string, string]> = {
        bold: ["*", "*"],
        italic: ["_", "_"],
        strikethrough: ["~", "~"],
        monospace: ["```", "```"],
        "inline-code": ["`", "`"],
      };
      const markers = formatMap[format];
      if (markers) {
        chipEditorRef.current.wrapSelection(markers[0], markers[1]);
        return;
      }
      // Line-level formatting
      const lineFormatMap: Record<
        string,
        "unordered" | "ordered" | "blockquote"
      > = {
        "unordered-list": "unordered",
        "ordered-list": "ordered",
        blockquote: "blockquote",
      };
      const listType = lineFormatMap[format];
      if (listType) {
        chipEditorRef.current.formatLines(listType);
      }
    }, []);

    const handleFormat = useCallback(
      (format: FormatType) => {
        if (isChipMode) {
          handleFormatChipMode(format);
          return;
        }
        switch (format) {
          case "bold":
            insertTextAtCursor("*", "*");
            break;
          case "italic":
            insertTextAtCursor("_", "_");
            break;
          case "strikethrough":
            insertTextAtCursor("~", "~");
            break;
          case "monospace":
            insertTextAtCursor("```", "```");
            break;
          case "inline-code":
            insertTextAtCursor("`", "`");
            break;
          case "unordered-list":
            formatLinesWithPrefix("unordered");
            break;
          case "ordered-list":
            formatLinesWithPrefix("ordered");
            break;
          case "blockquote":
            formatLinesWithPrefix("blockquote");
            break;
          default:
            break;
        }
      },
      [
        insertTextAtCursor,
        formatLinesWithPrefix,
        isChipMode,
        handleFormatChipMode,
      ],
    );

    const handleEmojiSelect = useCallback(
      (emoji: string) => {
        if (isChipMode && chipEditorRef.current) {
          chipEditorRef.current.insertAtCursor(emoji);
          return;
        }
        insertTextAtCursor(emoji);
      },
      [insertTextAtCursor, isChipMode],
    );

    // Border color - matches Input component
    const getBorderColor = () => {
      if (variant === "borderless") return "transparent";
      if (error) return colors.error;
      if (isFocused) return colors.primary;
      if (isFilled) return colors.surfaceDark;
      return colors.surface;
    };

    // Background color - matches Input component
    const getBackgroundColor = () => {
      if (variant === "borderless") return "transparent";
      if (disabled) return colors.surfaceLighter;
      if (isFocused) return colors.white;
      return variant === "filled" ? colors.surfaceLight : colors.white;
    };

    // Border width - exact same logic as Input component
    const getBorderWidth = () => {
      if (variant === "borderless") return 0;
      return isFocused || error ? 2 : 1;
    };

    const config = sizeConfig[size];

    // Container style - matches Input component exactly
    const containerStyle: React.CSSProperties = {
      borderColor: getBorderColor(),
      borderWidth: getBorderWidth(),
      borderStyle: "solid",
      backgroundColor: getBackgroundColor(),
      borderRadius: 3,
      transition: "border-color 0.15s ease, background-color 0.15s ease",
    };

    return (
      <div className={cn(fullWidth && "w-full", className)}>
        {label && (
          <label
            className="inline-flex items-center gap-1 mb-1.5 text-sm font-medium"
            style={{ color: colors.textDefault }}
          >
            <span>{label}</span>
            {required && <span style={{ color: colors.error }}>*</span>}
          </label>
        )}

        <div
          className="relative"
          style={{
            ...containerStyle,
            overflow: resizable ? "visible" : "hidden",
          }}
        >
          {isChipMode ? (
            <VariableChipEditor
              ref={chipEditorRef}
              value={currentValue}
              onChange={(newVal) => {
                if (value === undefined) {
                  setInternalValue(newVal);
                }
                onChange?.(newVal);
              }}
              onFocus={handleChipEditorFocus}
              onBlur={handleChipEditorBlur}
              variableLabelMap={variableLabelMap!}
              placeholder={placeholder}
              disabled={disabled}
              maxLength={maxLength}
              style={{
                fontSize: config.fontSize,
                padding: config.padding,
                paddingBottom:
                  !showToolbar && showCount && maxLength ? 28 : config.padding,
                backgroundColor: "transparent",
                minHeight: rows ? `${rows * 1.6}em` : undefined,
              }}
            />
          ) : (
            <TextArea
              ref={textAreaRef}
              id={id}
              value={currentValue}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={placeholder}
              disabled={disabled}
              rows={rows}
              autoSize={autoSize}
              maxLength={maxLength}
              variant="borderless"
              style={{
                fontSize: config.fontSize,
                padding: config.padding,
                paddingBottom:
                  !showToolbar && showCount && maxLength ? 28 : config.padding,
                resize: resizable ? "vertical" : "none",
                backgroundColor: "transparent",
                minHeight: resizable ? 80 : undefined,
              }}
            />
          )}

          {showToolbar &&
            (() => {
              const hasFormatButtons =
                formatButtons && formatButtons.length > 0;
              const hasToolbarContent =
                hasFormatButtons || showEmojiButton || showCount;

              // Only toolbarExtra, no format buttons/emoji/count — render extra alone
              if (!hasToolbarContent && toolbarExtra) {
                return (
                  <div className="flex items-center justify-end px-2 py-1">
                    {toolbarExtra}
                  </div>
                );
              }

              return toolbarExtra ? (
                <div className="flex items-center">
                  <FormattingToolbar
                    onFormat={handleFormat}
                    onEmojiSelect={handleEmojiSelect}
                    showEmojiPicker={showEmojiPicker}
                    onEmojiPickerChange={setShowEmojiPicker}
                    characterCount={currentValue.length}
                    maxCharacters={maxLength}
                    showCharacterCount={showCount}
                    showEmojiButton={showEmojiButton}
                    formatButtons={formatButtons}
                    disabledFormats={disabledFormats}
                    hiddenFormats={hiddenFormats}
                    size={size}
                  />
                  <div className="ml-auto shrink-0 pr-2">{toolbarExtra}</div>
                </div>
              ) : (
                <FormattingToolbar
                  onFormat={handleFormat}
                  onEmojiSelect={handleEmojiSelect}
                  showEmojiPicker={showEmojiPicker}
                  onEmojiPickerChange={setShowEmojiPicker}
                  characterCount={currentValue.length}
                  maxCharacters={maxLength}
                  showCharacterCount={showCount}
                  showEmojiButton={showEmojiButton}
                  formatButtons={formatButtons}
                  disabledFormats={disabledFormats}
                  hiddenFormats={hiddenFormats}
                  size={size}
                />
              );
            })()}

          {/* Character count when toolbar is hidden - positioned at bottom right */}
          {!showToolbar && showCount && maxLength && (
            <div
              className="absolute bottom-1 right-3 text-xs pointer-events-none"
              style={{
                color:
                  currentValue.length >= maxLength
                    ? colors.error
                    : colors.textMuted,
              }}
            >
              {currentValue.length}/{maxLength}
            </div>
          )}
        </div>

        {helperText && !error && (
          <div className="mt-1 text-xs" style={{ color: colors.textMuted }}>
            {helperText}
          </div>
        )}
        {error && (
          <div className="mt-1 text-xs" style={{ color: colors.error }}>
            {error}
          </div>
        )}
      </div>
    );
  },
);

RichTextArea.displayName = "RichTextArea";

export default RichTextArea;
